import { useEffect, useRef, useState } from 'react';
import { useDialogFocus } from '@/lib/useDialogFocus';

interface Props {
  file: File;
  /** Final output size in px. Square. */
  outputSize?: number;
  /** JPEG quality 0..1. */
  quality?: number;
  /** Called when the user confirms — gives back a JPEG Blob ready to upload. */
  onConfirm: (blob: Blob) => void;
  onClose: () => void;
}

/**
 * Minimal square-crop modal (no dependencies). Loads the file into an
 * <img>, lets the user drag the image inside a circular viewport AND
 * zoom via a slider. On Confirm, renders the visible region to a canvas
 * at outputSize (default 500) and exports as JPEG at quality (default
 * 0.88).
 *
 * The drag uses pointer events so it works on trackpad + touch. Zoom
 * range is 1× (image fills viewport short side) to 4×.
 */
export function ImageCropModal({
  file,
  outputSize = 500,
  quality = 0.88,
  onConfirm,
  onClose,
}: Props) {
  const formRef = useDialogFocus<HTMLDivElement>();
  const VIEWPORT = 280; // px on screen
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [src, setSrc] = useState<string>('');
  // natural image dims after load
  const [nat, setNat] = useState<{ w: number; h: number } | null>(null);
  // The image is positioned by translate(x,y) px relative to a centered base
  // where the image's short side equals VIEWPORT (zoom 1×).
  const [scale, setScale] = useState(1); // multiplier on top of base fit
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);
  const dragRef = useRef<{ startX: number; startY: number; baseX: number; baseY: number } | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const url = URL.createObjectURL(file);
    setSrc(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  function onImageLoad() {
    const img = imgRef.current;
    if (!img) return;
    setNat({ w: img.naturalWidth, h: img.naturalHeight });
    setScale(1);
    setTx(0);
    setTy(0);
  }

  /** "Base fit" — image scaled so its SHORT side equals VIEWPORT.
   *  Display dimensions at scale=1. */
  function baseFit(): { w: number; h: number } {
    if (!nat) return { w: VIEWPORT, h: VIEWPORT };
    const aspect = nat.w / nat.h;
    if (aspect >= 1) {
      // wider than tall — short side is height
      return { h: VIEWPORT, w: VIEWPORT * aspect };
    }
    return { w: VIEWPORT, h: VIEWPORT / aspect };
  }

  function onPointerDown(e: React.PointerEvent) {
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    dragRef.current = { startX: e.clientX, startY: e.clientY, baseX: tx, baseY: ty };
  }
  function onPointerMove(e: React.PointerEvent) {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    setTx(dragRef.current.baseX + dx);
    setTy(dragRef.current.baseY + dy);
  }
  function onPointerUp(e: React.PointerEvent) {
    (e.target as HTMLElement).releasePointerCapture(e.pointerId);
    dragRef.current = null;
  }

  async function confirm() {
    if (!nat) return;
    setBusy(true);
    try {
      const { w: dispW, h: dispH } = baseFit();
      const displayW = dispW * scale;
      const displayH = dispH * scale;
      // The image's top-left in viewport coordinates. The viewport is
      // centered at (VIEWPORT/2, VIEWPORT/2). At rest (tx=ty=0), the
      // image is centered too, so its top-left is at
      // (VIEWPORT/2 - displayW/2, VIEWPORT/2 - displayH/2) + (tx, ty).
      const imgLeftInVp = VIEWPORT / 2 - displayW / 2 + tx;
      const imgTopInVp = VIEWPORT / 2 - displayH / 2 + ty;
      // We want the natural-pixel region of the image that corresponds to
      // viewport [0, VIEWPORT) on both axes. Map vp → display → natural.
      const naturalPerDispX = nat.w / displayW;
      const naturalPerDispY = nat.h / displayH;
      const srcX = (0 - imgLeftInVp) * naturalPerDispX;
      const srcY = (0 - imgTopInVp) * naturalPerDispY;
      const srcSize = VIEWPORT * naturalPerDispX; // square
      const canvas = document.createElement('canvas');
      canvas.width = outputSize;
      canvas.height = outputSize;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Canvas 2D unavailable');
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(imgRef.current!, srcX, srcY, srcSize, srcSize, 0, 0, outputSize, outputSize);
      const blob: Blob = await new Promise((resolve, reject) =>
        canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob failed'))), 'image/jpeg', quality),
      );
      onConfirm(blob);
    } catch (e) {
      // Worst case the upload helper will surface a clearer error.
      console.error('crop failed', e);
    } finally {
      setBusy(false);
    }
  }

  const fit = baseFit();
  const dispW = fit.w * scale;
  const dispH = fit.h * scale;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="crop-title"
      className="fixed inset-0 z-[60] grid place-items-center bg-black/80 px-4"
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
    >
      <div
        ref={formRef}
        tabIndex={-1}
        className="section-glow w-full max-w-md space-y-4 rounded-2xl bg-card p-6 shadow-2xl"
      >
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            Crop avatar
          </div>
          <h2 id="crop-title" className="text-lg font-semibold tracking-tight">
            Drag to position · scroll to zoom
          </h2>
        </div>

        {/* Square viewport with a circular mask so the user sees the
            avatar shape they'll end up with. */}
        <div
          className="relative mx-auto select-none overflow-hidden bg-background"
          style={{
            width: VIEWPORT,
            height: VIEWPORT,
            cursor: dragRef.current ? 'grabbing' : 'grab',
            borderRadius: '50%',
          }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onWheel={(e) => {
            const next = Math.max(1, Math.min(4, scale - e.deltaY * 0.002));
            setScale(next);
          }}
        >
          {src && (
            <img
              ref={imgRef}
              src={src}
              alt=""
              onLoad={onImageLoad}
              draggable={false}
              style={{
                position: 'absolute',
                width: dispW,
                height: dispH,
                left: VIEWPORT / 2 - dispW / 2,
                top: VIEWPORT / 2 - dispH / 2,
                transform: `translate(${tx}px, ${ty}px)`,
                pointerEvents: 'none',
                userSelect: 'none',
              }}
            />
          )}
        </div>

        <div className="space-y-1">
          <label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            Zoom
          </label>
          <input
            type="range"
            min={1}
            max={4}
            step={0.01}
            value={scale}
            onChange={(e) => setScale(Number(e.target.value))}
            className="no-glow w-full accent-primary"
            aria-label="Zoom"
          />
        </div>

        <p className="text-[10px] text-muted-foreground">
          Output: {outputSize}×{outputSize}px · auto-compressed JPEG · ≤4MB upload cap.
        </p>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-md border border-border bg-background px-4 py-2 text-sm text-muted-foreground hover:border-ring/60 hover:text-foreground"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={confirm}
            disabled={busy || !nat}
            className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
          >
            {busy ? 'Processing…' : 'Use photo'}
          </button>
        </div>
      </div>
    </div>
  );
}
